import { useCallback, useEffect, useState } from 'react';
import { useBlocker, useNavigate, useParams } from 'react-router';
import { TooltipProvider } from '@monksflow/monks-ui';
import { getElementSnapshot, type SectionPickerClickPayload } from '@/entities/section-picker';
import { useKnowledgeBaseImages } from '@/features/knowledge-bases';
import {
  ConfigureContentElementSheet,
  CustomInstructionsSheet,
  EditableContentsPanel,
  EmailTemplatesErrorState,
  useEmailTemplateMutations,
  useEmailTemplates,
  TemplateNameSheet,
  useTemplateEditorState,
  type ConfigureElementTarget,
  type TemplateItem,
} from '@/features/templates-config';
import { Loading } from '@/shared/ui';
import { UnsavedChangesModal } from '@/shared/ui/UnsavedChangesModal';
import { ROUTES } from '@/shared/config/routes';
import { notificationService } from '@/lib/services/notification.service';
import { useTranslation } from 'react-i18next';
import { TemplateEditorHeader } from './components/TemplateEditorHeader';
import { TemplateEditorSurface } from './components/TemplateEditorSurface';

/**
 * Mounted only once the template has actually loaded.
 *
 * That is load-bearing, not tidiness: `useTemplateEditorState` picks the default view mode in a
 * once-only initializer. Mounting this during the loading window would seed it from an empty
 * string and freeze every template on the code editor, since the component is keyed by slug and
 * never remounts when the data lands.
 */
function TemplateEditorView({ template }: { template: TemplateItem }) {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();

  const { saveTemplateHtml, renameTemplate, saveComplementaryPrompt } = useEmailTemplateMutations();

  const editor = useTemplateEditorState({ savedHtml: template.html });
  const [target, setTarget] = useState<ConfigureElementTarget | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [customInstructionsOpen, setCustomInstructionsOpen] = useState(false);
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);

  // Catches every route exit — Back, the breadcrumb, a sidebar link, the browser's back button —
  // rather than only the two affordances this header happens to render. The draft lives in local
  // state behind an explicit Save, so leaving without one silently discards it.
  const blocker = useBlocker(editor.isDirty);

  // `useBlocker` only sees router navigations. A refresh, a tab close, or typing a new URL bypasses
  // it entirely and the draft — which lives in local state behind an explicit Save — just goes.
  // `preventDefault()` is the specified trigger; `returnValue` is legacy but still what Chrome and
  // Edge below 119 read. Scoped to `isDirty` so a clean editor never interrupts a refresh.
  useEffect(() => {
    if (!editor.isDirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Deprecated on purpose, not overlooked: `preventDefault()` alone does not raise the dialog on
      // Chrome/Edge below 119, which still read this. Drop the line once that floor moves.
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- see above
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [editor.isDirty]);

  const handleElementClick = useCallback(
    (payload: SectionPickerClickPayload) => {
      // From the SAVED html, never the click payload: the payload was serialised out of the live
      // preview DOM, which the runtime has stamped with `data-updatable-tagged` — editing through it
      // wrote that marker back into the template as a phantom section. Payload is the fallback for a
      // selector that does not resolve. Read once here; during render it would re-parse the whole
      // template on every keystroke the open modal causes.
      const snapshot = getElementSnapshot(editor.html, payload.selector);
      setTarget({
        selector: payload.selector,
        tagName: snapshot?.tagName ?? payload.tagName.toLowerCase(),
        // The picker reports the whole attribute name; the rest of the app keys off the slug.
        sectionName: payload.alreadyTagged?.replace('data-updatable-', '') ?? null,
        innerHTML: snapshot?.innerHTML ?? payload.innerHTML,
        textContent: snapshot?.textContent ?? payload.fullTextContent,
        hasChildElements: snapshot?.hasChildElements ?? payload.hasChildElements,
        attributes: snapshot?.attributes ?? [],
      });
    },
    [editor.html],
  );

  // Opening from the side panel has no click payload, so the element is read back out of the HTML
  // by its slug selector — which is exactly what a tagged element's click selector would have been.
  const handleEditSection = useCallback(
    (sectionName: string) => {
      const selector = `[data-updatable-${sectionName}]`;
      const snapshot = getElementSnapshot(editor.html, selector);
      if (!snapshot) {
        notificationService.showError(t('templatesConfig.editor.sectionNotFound', { sectionName }));
        return;
      }
      setTarget({ selector, sectionName, ...snapshot });
    },
    [editor.html, t],
  );

  const handleSave = useCallback(() => {
    // Snapshotted, not re-read on success: the draft can move on during the round-trip, and only the
    // version that actually reached the server may clear the dirty flag.
    const sent = editor.html;
    saveTemplateHtml.mutate(
      { slug: template.slug, html: sent },
      { onSuccess: () => editor.markSaved(sent) },
    );
  }, [template, saveTemplateHtml, editor]);

  return (
    <TooltipProvider>
      <div className="flex h-full min-h-0 flex-col gap-app-inset">
        <TemplateEditorHeader
          templateName={template.templateName}
          canSave={editor.isDirty && !saveTemplateHtml.isPending}
          isSaving={saveTemplateHtml.isPending}
          onBack={() => void navigate(ROUTES.KNOWLEDGE_HUB_TEMPLATES)}
          onSave={handleSave}
        />

        <div className="flex min-h-0 flex-1 gap-app-inset">
          <TemplateEditorSurface
            templateName={template.templateName}
            html={editor.html}
            viewMode={editor.viewMode}
            mobilePreview={editor.mobilePreview}
            onViewModeChange={editor.setViewMode}
            onMobilePreviewChange={editor.setMobilePreview}
            onHtmlChange={editor.updateHtml}
            onElementClick={handleElementClick}
            onRenameClick={() => setRenameOpen(true)}
            onCustomInstructionsClick={() => setCustomInstructionsOpen(true)}
            hasCustomInstructions={!!template.complementaryPrompt?.trim()}
            highlightedSection={hoveredSection}
          />

          <aside className="w-80 shrink-0 overflow-auto rounded-xl border border-border bg-background p-section">
            <EditableContentsPanel
              sections={editor.sections}
              hasHtml={editor.html.trim().length > 0}
              disabled={editor.viewMode === 'code'}
              onEdit={handleEditSection}
              onRemove={editor.removeSection}
              onHoverSection={setHoveredSection}
            />
          </aside>
        </div>
      </div>

      {target && (
        <ConfigureContentElementSheet
          target={target}
          html={editor.html}
          existingSections={editor.sections}
          onClose={() => setTarget(null)}
          onApply={editor.updateHtml}
          useKnowledgeBaseImagesHook={useKnowledgeBaseImages}
        />
      )}

      <UnsavedChangesModal
        open={blocker.state === 'blocked'}
        onCancel={() => blocker.reset?.()}
        onConfirmLeave={() => blocker.proceed?.()}
      />

      {customInstructionsOpen && (
        <CustomInstructionsSheet
          initialValue={template.complementaryPrompt ?? ''}
          isSaving={saveComplementaryPrompt.isPending}
          onCancel={() => setCustomInstructionsOpen(false)}
          onSubmit={(complementaryPrompt) =>
            saveComplementaryPrompt.mutate(
              { slug: template.slug, complementaryPrompt },
              { onSuccess: () => setCustomInstructionsOpen(false) },
            )
          }
        />
      )}

      {renameOpen && (
        <TemplateNameSheet
          mode="rename"
          initialName={template.templateName}
          onCancel={() => setRenameOpen(false)}
          onSubmit={(templateName) => {
            renameTemplate.mutate({ slug: template.slug, templateName });
            setRenameOpen(false);
          }}
        />
      )}
    </TooltipProvider>
  );
}

export function EmailTemplateEditor() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const { templateSlug } = useParams<{ templateSlug: string }>();
  const { templates, isLoading, isError, refetch } = useEmailTemplates();

  const template = templates.find((item) => item.slug === templateSlug);

  // "Absent" only means deleted once we know the list actually loaded. Without the `isError`
  // guard a failed request produced an empty array and the user was told their template no
  // longer exists — the one explanation guaranteed to be wrong during an outage.
  const missing = !isLoading && !isError && !template;
  useEffect(() => {
    if (!missing) return;
    notificationService.showError(t('templatesConfig.editor.templateNotFound'));
    void navigate(ROUTES.KNOWLEDGE_HUB_TEMPLATES, { replace: true });
  }, [missing, navigate, t]);

  if (isError && !template) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <EmailTemplatesErrorState onRetry={() => void refetch()} />
      </div>
    );
  }

  if (!template) return <Loading />;

  // Keyed on the slug so switching templates remounts the editor and reseeds its draft and default
  // view mode, rather than carrying one template's state into another.
  return <TemplateEditorView key={template.slug} template={template} />;
}
